import { ErrorBoundary as InternalErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useTranslation } from 'react-i18next';
import { useCanvasContext } from '../hooks/useCanvasContext';

type ErrorBoundaryProps = {
  children: React.ReactNode;
};

const ErrorFallback = (boundaryProps: FallbackProps) => {
  const { t } = useTranslation();
  const { isCanvas, getCleanUrl } = useCanvasContext();

  return (
    <div
      className={`${isCanvas ? 'h-screen overflow-hidden' : 'min-h-screen'} bg-background text-foreground flex items-center justify-center p-4`}
    >
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-destructive/10 rounded-full">
              <AlertTriangle className="h-8 w-8 text-destructive" />
            </div>
          </div>
          <CardTitle className="text-xl">{t('errorBoundary.title')}</CardTitle>
          <p className="text-sm text-muted-foreground">{t('errorBoundary.description')}</p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Error Details */}
          <div className="p-3 bg-muted rounded-md">
            <p className="text-sm font-mono text-destructive">{boundaryProps.error.message}</p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              onClick={() => boundaryProps.resetErrorBoundary()}
              className="flex-1"
              variant="outline"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              {t('errorBoundary.tryAgain')}
            </Button>
            {!isCanvas && (
              <Button onClick={() => (window.location.href = getCleanUrl('/'))} className="flex-1">
                <Home className="h-4 w-4 mr-2" />
                {t('errorBoundary.goHome')}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const ErrorBoundary = ({ children }: ErrorBoundaryProps) => {
  return (
    <InternalErrorBoundary FallbackComponent={ErrorFallback}>{children}</InternalErrorBoundary>
  );
};

export default ErrorBoundary;
